import { Crypto } from "../../../models/Crypto.ts";
import { Crypto as Crypto2 } from "../../../managers/Crypto.ts";
import template from "./hb-locker.mjs";
import { render } from "lit-html";
import { HbMain } from "../../hb-main/hb-main.ts";
import { getLocker, pickFile, redirect } from "../../global.ts";
import { HeartBank } from "../../../managers/HeartBank.ts";
import { User } from "firebase/auth";

import { Modal } from "flowbite";
import type { ModalOptions, ModalInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

export class HbLocker extends HTMLElement {
    private heartbank = new HeartBank();
    private crypto: Crypto = new Crypto();
    private crypto2 = new Crypto2();
    private modal: ModalInterface;
    private main: HbMain;

    private locker: HTMLElement;
    private trustkeys: HTMLUListElement;
    private create: HTMLButtonElement;
    private add: HTMLButtonElement;
    private import: HTMLButtonElement;
    private download: HTMLButtonElement;
    private clear: HTMLButtonElement;
    private message: HTMLParagraphElement;

    private dialog: HTMLDialogElement;
    private dialogMessage: HTMLParagraphElement;
    private form: HTMLFormElement;
    private submit: HTMLButtonElement;
    private close: HTMLButtonElement;
    private name: HTMLInputElement;
    private namePaste: HTMLButtonElement;
    private address: HTMLInputElement;
    private addressPaste: HTMLButtonElement;
    private key: HTMLInputElement;
    private keyPaste: HTMLButtonElement;

    private format: "pem" | "json" = "pem"; // pem only; json not used

    constructor() {
        super();
        render(template(null), this);
        this.main = document.querySelector("hb-main") as HbMain;

        this.locker = this.querySelector("#hb-locker")!;
        this.trustkeys = this.querySelector("#hb-locker-trustkeys")!;
        this.create = this.querySelector("#hb-locker-create")!;
        this.add = this.querySelector("#hb-locker-add")!;
        this.import = this.querySelector("#hb-locker-import")!;
        this.download = this.querySelector("#hb-locker-download")!;
        this.clear = this.querySelector("#hb-locker-clear")!;
        this.message = this.querySelector("#hb-locker-message")!;

        this.dialog = this.querySelector("#hb-locker-dialog")!;
        this.dialogMessage = this.querySelector("#hb-locker-dialog-message")!;
        this.form = this.querySelector("#hb-locker-dialog-form")!;
        this.submit = this.querySelector("#hb-locker-dialog-submit")!;
        this.close = this.querySelector("#hb-locker-dialog-close")!;
        this.name = this.querySelector("#hb-locker-dialog-name")!;
        this.namePaste = this.querySelector("#hb-locker-dialog-name-paste")!;
        this.address = this.querySelector("#hb-locker-dialog-address")!;
        this.addressPaste = this.querySelector(
            "#hb-locker-dialog-address-paste"
        )!;
        this.key = this.querySelector("#hb-locker-dialog-key")!;
        this.keyPaste = this.querySelector("#hb-locker-dialog-key-paste")!;

        const modalOptions: ModalOptions = {
            placement: "center",
            backdrop: "dynamic",
            backdropClasses:
                "bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40",
            closable: true,
            onHide: () => {
                //window.location.hash = "#";
            },
            onShow: () => {
                //console.log("modal is shown");
            },
            onToggle: () => {
                //console.log("modal has been toggled");
            }
        };

        // instance options object
        const instanceOptions: InstanceOptions = {
            id: "hb-locker",
            override: true
        };

        this.modal = new Modal(this.locker, modalOptions, instanceOptions);
    }

    connectedCallback() {
        //this.render(); // redunant

        this.create.addEventListener("click", this.createTrustkey.bind(this));
        this.import.addEventListener("click", this.importAll.bind(this));
        this.download.addEventListener(
            "click",
            this.downloadAll.bind(this, this.format)
        );
        this.clear.addEventListener("click", this.clearAll.bind(this));

        this.add.addEventListener("click", () => {
            this.style.opacity = "0.5";
            this.dialog.showModal();
        });
        this.close.addEventListener("click", () => {
            this.style.opacity = "";
            this.dialog.close();
        });
        this.form.addEventListener("submit", this.addTrustkey.bind(this));

        this.namePaste.addEventListener(
            "click",
            async () => (this.name.value = await navigator.clipboard.readText())
        );
        this.addressPaste.addEventListener(
            "click",
            async () =>
                (this.address.value = await navigator.clipboard.readText())
        );
        this.keyPaste.addEventListener(
            "click",
            async () => (this.key.value = await navigator.clipboard.readText())
        );

        this.heartbank.addAuthStateListener((user) => {
            this.render(user);
        });
    }

    async render(user: User | null = null) {
        this.main.renderSilver(user); // await
        const sorted = await this.crypto.getAllKeys({
            sort: (a: any, b: any) => a.name.localeCompare(b.name)
        });

        if (sorted.length === 0) {
            this.trustkeys.innerHTML =
                "<p class='p-3'>There are currently no Lockers stored on this device</p>";
            this.trustkeys.style.color = "gray";
        } else {
            this.trustkeys.innerHTML = "";
            this.trustkeys.style.color = "";

            sorted.forEach((key: any, index) => {
                const li = document.createElement("li");
                li.classList.add(
                    index === sorted.length - 1 ? "mb-0" : "mb-4",
                    "bg-white",
                    "dark:bg-gray-800",
                    "border",
                    "border-gray-200",
                    "dark:border-gray-700",
                    "shadow-sm",
                    "rounded-lg",
                    "p-4"
                );

                const button1 = document.createElement("button");
                button1.innerHTML = "🔐&nbsp;&nbsp;" + key.name.truncate2(20);
                button1.classList.add("ml-1");
                button1.onclick = async () => {
                    const newName = prompt(
                        "New name for your Locker:",
                        key.name
                    )?.trim();
                    if (newName && newName !== key.name) {
                        await this.crypto.rename(key.id, newName);
                        this.message.textContent = `${key.name} has been renamed to ${newName}`;
                        this.message.style.color = "green";
                        this.render(user);
                    }
                };

                const button2a = document.createElement("button");
                if (user) {
                    const isPrimary = getLocker() === key.publicKey;
                    if (!isPrimary) button2a.classList.add("opacity-30");
                    button2a.textContent = "🌐";
                    button2a.classList.add("mr-5");
                    button2a.onclick = async () => {
                        if (isPrimary) {
                            this.message.textContent =
                                key.name +
                                " is your Primary Locker which means it is publicly tied to your profile for others to discover. You can rotate it out at anytime to dissociate it to your profle.";
                            this.message.style.color = "orange";
                        } else {
                            if (
                                confirm(
                                    `Would you like to make ${key.name} your Primary Locker? This means it will be publicly tied to your profile for others to discover.`
                                )
                            ) {
                                const { text, color } =
                                    await this.crypto2.update(
                                        user.uid,
                                        key.publicKey
                                    );
                                this.message.textContent =
                                    color === "green"
                                        ? key.name +
                                          " is now your Primary Locker. Going forward, your previous Primary Locker will no longer be associated to your profile."
                                        : text;
                                this.message.style.color = color;
                                this.render(user);
                            }
                        }
                    };
                }
                const button2b = document.createElement("button");
                button2b.textContent = "🗑️";
                button2b.classList.add("mr-0");
                button2b.onclick = async () => {
                    if (
                        confirm(
                            `Would you like to remove ${key.name} from this device?`
                        )
                    ) {
                        await this.crypto.remove(key.id);
                        this.message.textContent =
                            key.name + " has been deleted from this device.";
                        this.message.style.color = "green";
                        this.render(user);
                    }
                };
                const aside = document.createElement("aside");
                aside.append(button2a, button2b);

                const h2 = document.createElement("h2");
                h2.classList.add(
                    "w-full",
                    "inline-flex",
                    "justify-between",
                    "text-lg",
                    "font-semibold",
                    "text-gray-900",
                    "dark:text-white",
                    "mb-2"
                );
                h2.append(button1, aside);

                const label1 = document.createElement("label");
                label1.classList.add(
                    "block",
                    "my-2",
                    "text-sm",
                    "font-medium",
                    "text-gray-900",
                    "dark:text-white"
                );
                label1.textContent = "Locker Address ℹ️";
                const label2 = label1.cloneNode(false) as HTMLLabelElement;
                label2.textContent = "Private Key ℹ️";

                const input1 = document.createElement("input");
                input1.type = "text";
                input1.classList.add(
                    "row-span-6",
                    "bg-gray-50",
                    "border",
                    "border-gray-300",
                    "text-gray-500",
                    "text-sm",
                    "rounded-lg",
                    "focus:ring-blue-500",
                    "focus:border-blue-500",
                    "block",
                    "w-full",
                    "p-2.5",
                    "dark:bg-gray-800",
                    "dark:border-gray-600",
                    "dark:placeholder-gray-400",
                    "dark:text-gray-400",
                    "dark:focus:ring-blue-500",
                    "dark:focus:border-blue-500"
                );
                input1.disabled = true;
                input1.readOnly = true;
                input1.value = key.publicKey.truncate(11, 11);
                const input2 = input1.cloneNode(false) as HTMLInputElement;
                input2.value = key.privateKey.truncate(11, 11);

                const button3 = document.createElement("button");
                button3.classList.add(
                    "cursor-pointer",
                    "w-3.5",
                    "h-3.5",
                    "absolute",
                    "end-2",
                    "top-1/2",
                    "-translate-y-1/2",
                    "text-gray-500",
                    "dark:text-gray-400",
                    "hover:bg-gray-100",
                    "dark:hover:bg-gray-800",
                    "rounded-lg",
                    "p-2",
                    "inline-flex",
                    "items-center",
                    "justify-center"
                );
                button3.onclick = async () => {
                    await navigator.clipboard.writeText(key.publicKey);
                    this.message.textContent =
                        key.name +
                        "'s Locker Address has been copied to your clipboard! Feel free to share it with others.";
                    this.message.style.color = "green";
                };
                button3.textContent = "📋";
                // doesn't work
                // button3.innerHTML = `<svg
                //                         class="w-3.5 h-3.5"
                //                         aria-hidden="true"
                //                         xmlns="http://www.w3.org/2000/svg"
                //                         fill="currentColor"
                //                         viewBox="0 0 18 20">
                //                         <path
                //                             d="M16 1h-3.278A1.992 1.992 0 0 0 11 0H7a1.993 1.993 0 0 0-1.722 1H2a2 2 0 0 0-2 2v15a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2Zm-3 14H5a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2Zm0-4H5a1 1 0 0 1 0-2h8a1 1 0 1 1 0 2Zm0-5H5a1 1 0 0 1 0-2h2V2h4v2h2a1 1 0 1 1 0 2Z" />
                //                     </svg>`;

                const button4 = button3.cloneNode(true) as HTMLButtonElement;
                button4.onclick = async () => {
                    await navigator.clipboard.writeText(key.privateKey);
                    this.message.textContent =
                        key.name +
                        "'s Private Key has been copied to your clipboard! Please store it securely and never share it with anyone.";
                    this.message.style.color = "green";
                };

                const div1 = document.createElement("div");
                div1.classList.add("relative", "mb-5");
                div1.append(input1, button3);

                const div2 = document.createElement("div");
                div2.classList.add("relative", "mb-3");
                div2.append(input2, button4);

                const address = document.createElement("address");
                address.classList.add(
                    "bg-gray-50",
                    "dark:bg-gray-700",
                    "dark:border-gray-600",
                    "p-4",
                    "rounded-lg",
                    "border",
                    "border-gray-200",
                    "not-italic",
                    "flex-row"
                );
                address.append(label1, div1, label2, div2);

                li.append(h2, address);
                this.trustkeys.appendChild(li);
            });
        }
    }

    // deprecated
    async render2() {
        await this.main.renderSilver(this.heartbank.user);

        // All keys sorted by name
        const sorted = await this.crypto.getAllKeys({
            sort: (a: any, b: any) => a.name.localeCompare(b.name)
        });
        // Filter keys created in the last day
        // const recent = await this.crypto.getAllKeys({
        //     filter: (k: any) => k.createdAt > Date.now() - 86400_000
        // });

        if (sorted.length === 0) {
            this.trustkeys.innerHTML =
                "There are currently no Lockers stored on this device";
            this.trustkeys.style.color = "gray";
        } else {
            this.trustkeys.innerHTML = "";
            this.trustkeys.style.color = "";

            sorted.forEach((key: any) => {
                const li = document.createElement("li");
                li.classList.add("flex", "items-center", "justify-between");

                const span = document.createElement("span");
                span.textContent = "🔐 " + key.name;
                span.classList.add("cursor-pointer");
                span.onclick = async () => {
                    const newName = prompt(
                        "New name for your Locker:",
                        key.name
                    )?.trim();
                    if (newName && newName !== key.name) {
                        await this.crypto.rename(key.id, newName);
                        this.render2();
                    }
                };

                const publicBtn = document.createElement("button");
                publicBtn.classList.add("cursor-pointer");
                publicBtn.textContent = "📋 Public Locker";
                publicBtn.onclick = async () => {
                    await navigator.clipboard.writeText(key.publicKey);
                    this.message.textContent =
                        key.name +
                        "'s Public Locker has been copied to your clipboard! Feel free to share it with others.";
                    this.message.style.color = "green";
                };

                const privateBtn = document.createElement("button");
                privateBtn.classList.add("cursor-pointer");
                privateBtn.textContent = "📋 Private Locker";
                privateBtn.onclick = async () => {
                    await navigator.clipboard.writeText(key.privateKey);
                    this.message.textContent =
                        key.name +
                        "'s Private Locker has been copied to your clipboard! Please store it securely and never share it with anyone.";
                    this.message.style.color = "green";
                };

                const deleteBtn = document.createElement("button");
                deleteBtn.classList.add("cursor-pointer");
                deleteBtn.textContent = "❌";
                deleteBtn.onclick = async () => {
                    await this.crypto.remove(key.id);
                    this.message.textContent =
                        key.name + " has been deleted from this device.";
                    this.message.style.color = "green";
                    this.render2();
                };

                li.append(span, " ", publicBtn, privateBtn, deleteBtn);
                this.trustkeys.appendChild(li);
            });
        }
    }

    private async createTrustkey() {
        this.add.disabled = true;
        this.message.textContent = "Creating Locker...";
        this.message.style.color = "orange";
        const name = prompt("Please enter a name for this Locker:");
        if (name) {
            await this.crypto.add(name);
            this.message.textContent = name + " was added successfully!";
            this.message.style.color = "green";
            this.render(this.heartbank.user);
            redirect("locker");
        } else {
            this.message.textContent = "";
        }
        this.add.disabled = false;
    }

    private async addTrustkey(event: Event) {
        event.preventDefault();
        this.submit.disabled = true;
        this.dialogMessage.textContent = "Adding Locker...";
        this.dialogMessage.style.color = "orange";

        // check key pair
        const name = this.name.value.trim();
        const address = this.address.value.trim();
        const key = this.key.value.trim();

        try {
            const isValid = await this.crypto.isValid(address, key);
            if (isValid) {
                const id = await this.crypto.add2(name, address, key);
                if (id) {
                    this.dialogMessage.textContent = "";
                    this.message.textContent =
                        name + " has been added to this device successfully";
                    this.message.style.color = "green";
                    this.style.opacity = "";
                    this.form.reset();
                    this.dialog.close();
                    this.render(this.heartbank.user);
                    redirect("locker");
                } else {
                    this.dialogMessage.textContent =
                        name + " already exists on this device";
                    this.dialogMessage.style.color = "red";
                }
            } else {
                this.dialogMessage.textContent =
                    "Sorry, the Locker Address and Private Key are not valid";
                this.dialogMessage.style.color = "red";
            }
        } catch (error: any) {
            console.log(error);
            this.dialogMessage.textContent =
                "The Locker Address and/or Private Key is not valid";
            this.dialogMessage.style.color = "red";
        } finally {
            this.submit.disabled = false;
        }
    }

    private async importAll() {
        this.import.disabled = true;
        this.message.textContent = "Please select a PEM file to import";
        this.message.style.color = "orange";
        try {
            const file = await pickFile({ accept: "." + this.format }); //".json,.pem"
            const format = file.name.split(".").pop()?.toLowerCase();
            if (format === "json") {
                this.message.textContent =
                    "Importing all Lockers stored in your JSON file...";
                //await this.crypto.importFromJSON(file);
            } else {
                this.message.textContent =
                    "Importing all Lockers stored in your PEM file...";
                await this.crypto.importFromPEM(file);
            }
            this.message.textContent =
                "All Lockers have successfully been added to this device!";
            this.message.style.color = "green";
            this.render(this.heartbank.user);
            redirect("locker");
        } catch (error: any) {
            this.message.textContent = "Failed to import: " + error.message;
            this.message.style.color = "red";
        } finally {
            this.import.disabled = false;
        }
    }

    private async downloadAll(format: "json" | "pem" = "pem") {
        this.download.disabled = true;
        this.message.textContent = "Downloading all Lockers...";
        this.message.style.color = "orange";
        await this.crypto.exportAll(); // { format }
        this.message.textContent =
            "All Lockers have been downloaded successfully! Please store them in a secure place.";
        this.message.style.color = "green";
        this.download.disabled = false;
    }
    private async clearAll() {
        this.clear.disabled = true;
        if (
            confirm(
                "Are you sure you want to delete all your Lockers from this device?"
            )
        ) {
            this.message.textContent =
                "Clearing all Lockers from this device...";
            this.message.style.color = "orange";
            await this.crypto.deleteAllKeys();
            this.message.textContent =
                "All Lockers have been deleted from this device!";
            this.message.style.color = "green";
            this.render(this.heartbank.user);
        }
        this.clear.disabled = false;
    }

    show() {
        this.modal.show();
    }

    hide() {
        this.modal.hide();
    }
}
